import { ref } from 'vue';
import { useImageCacheStore } from '@/src/store/image-cache';
import { useSegmentationStore } from '@/src/segmentation/store';
import { useSegmentStore } from '@/src/segmentation/segments';
import { boundScalars } from '@/src/segmentation/masks/overlap';
import { emptyExtent, growExtent, extentContainsIndex, maskOffset } from '@/src/segmentation/geometry';
import { observePaintStrokes } from '@/src/segmentation/editing/strokeEvents';
type Voxel = { i: number; j: number; k: number; before: string[]; after: string[] };
type Stroke = { imageId: string; voxels: Voxel[] };
const same = (a: string[], b: string[]) => a.length === b.length && a.every((id, i) => id === b[i]);

export function createBrushHistory(imageId: () => string, changed: () => void) {
  const undoCount = ref(0); const redoCount = ref(0); const notice = ref('');
  let undo: Stroke[] = []; let redo: Stroke[] = []; let captured = new Map<number, Voxel>(); let overflow = false;
  const segmentation = useSegmentationStore();
  function updateCounts() { undoCount.value = undo.length; redoCount.value = redo.length; }
  function clear() { undo = []; redo = []; captured.clear(); notice.value = ''; updateCounts(); }
  const dispose = observePaintStrokes({
    begin: id => { if (id === imageId()) { captured.clear(); overflow = false; } },
    beforeVoxel: (id, i, j, k) => {
      if (id !== imageId() || overflow) return;
      const dims = useImageCacheStore().getVtkImageData(id)!.getDimensions();
      const offset = i + dims[0] * (j + dims[1] * k);
      if (captured.has(offset)) return;
      if (captured.size >= 200000) { overflow = true; captured.clear(); return; }
      captured.set(offset, { i, j, k, before: segmentation.segmentsAt(id, i, j, k), after: [] });
    },
    end: id => {
      if (id !== imageId()) return;
      if (overflow) { clear(); notice.value = 'This stroke exceeded the undo memory limit; earlier stroke history was cleared.'; changed(); return; }
      const voxels = [...captured.values()].filter(v => { v.after = segmentation.segmentsAt(id, v.i, v.j, v.k); return !same(v.before, v.after); });
      captured.clear();
      if (!voxels.length) return;
      undo.push({ imageId: id, voxels }); redo = [];
      // Sparse changed voxels only; never copy a complete jaw per brush step.
      while (undo.length > 40 || undo.reduce((count, stroke) => count + stroke.voxels.length, 0) > 250000) undo.shift();
      updateCounts(); changed();
    },
  });
  function apply(stroke: Stroke, side: 'before' | 'after') {
    const ids = new Set(stroke.voxels.flatMap(v => [...v.before, ...v.after]));
    if ([...ids].some(id => useSegmentStore().segments.getSegment(id)?.locked)) {
      notice.value = 'Unlock affected instances before undoing or redoing this stroke.';
      return false;
    }
    for (const segmentId of ids) {
      const writes = stroke.voxels.filter(v => v[side].includes(segmentId));
      let mask = segmentation.maskFor(stroke.imageId, segmentId);
      if (!mask && !writes.length) continue;
      mask ??= segmentation.createMask(segmentation.ensureSegmentationForImage(stroke.imageId).id, segmentId);
      const voxels = segmentation.maskVoxels(mask.id); voxels.materialize();
      if (writes.length) {
        const extent = emptyExtent(); writes.forEach(v => growExtent(extent, v.i, v.j, v.k)); voxels.ensureContains(extent);
      }
      const bounded = boundScalars(voxels.binding());
      if (!bounded) continue;
      for (const v of stroke.voxels) if (extentContainsIndex(bounded.extent, v.i, v.j, v.k)) bounded.scalars[maskOffset(bounded, v.i, v.j, v.k)] = v[side].includes(segmentId) ? 1 : 0;
      voxels.image().modified();
    }
    changed();
    return true;
  }
  function undoStroke() { const stroke = undo.at(-1); if (stroke && apply(stroke, 'before')) { undo.pop(); redo.push(stroke); updateCounts(); } }
  function redoStroke() { const stroke = redo.at(-1); if (stroke && apply(stroke, 'after')) { redo.pop(); undo.push(stroke); updateCounts(); } }
  return { undoCount, redoCount, notice, clear, dispose, undoStroke, redoStroke };
}
